/**
 * REST client helpers for artifact GitHub publication endpoints.
 */
import { buildBackendUrl } from '@/lib/auth-routing';
import type {
  ArtifactPublication,
  ArtifactPublicationListResponse,
  ArtifactPublicationResponse,
  PublishArtifactBody,
} from './api';

async function readApiError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `API error ${String(res.status)}`;
  } catch {
    return `API error ${String(res.status)}`;
  }
}

async function fetchOrThrow(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(buildBackendUrl(url), {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
  if (!res.ok) {
    const err = new Error(await readApiError(res)) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res;
}

function jsonRequest(body: unknown): RequestInit {
  return {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export async function listPublications(artifactId: string): Promise<ArtifactPublication[]> {
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/github-publications`
  );
  const body = (await res.json()) as ArtifactPublicationListResponse;
  return body.publications;
}

export async function publishArtifact(
  artifactId: string,
  body: PublishArtifactBody
): Promise<ArtifactPublication> {
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/github-publications`,
    {
      method: 'POST',
      ...jsonRequest(body),
    }
  );
  const data = (await res.json()) as ArtifactPublicationResponse;
  return data.publication;
}

export async function getPublication(publicationId: string): Promise<ArtifactPublication> {
  const res = await fetchOrThrow(
    `/api/artifact-github-publications/${encodeURIComponent(publicationId)}`
  );
  const data = (await res.json()) as ArtifactPublicationResponse;
  return data.publication;
}

export async function checkDrift(publicationId: string): Promise<ArtifactPublication> {
  const res = await fetchOrThrow(
    `/api/artifact-github-publications/${encodeURIComponent(publicationId)}/check-drift`,
    { method: 'POST' }
  );
  const data = (await res.json()) as ArtifactPublicationResponse;
  return data.publication;
}
